'use client';

import { Divider, Spinner, Text } from '@stone-payments/jade';
import { useIntl } from 'react-intl';
import { useApplePayReducedForm } from '../../../hooks/useApplePayReducedForm';
import { usePreselectVariant } from '../../../hooks/usePreselectVariant';
import { customerDataType } from '../../../types/customer.types';
import { digitalWallets } from '../../types/digitalWallets.types';
import { DigitalWalletsRequiredFields } from '../digitalWallets/digitalWalletsRequiredFields.component';
import { CustomerForm } from './customerForm';

type PaymentDetailsSectionProps = {
  customer: customerDataType;
  sessionId: string;
  paymentLinkId: string;
  selectedDigitalWallet: digitalWallets | null;
  clickToPaySessionPending: boolean;
  isDigitalWalletValid: boolean;
  setIsDigitalWalletValid: (isValid: boolean) => void;
};

export function PaymentDetailsSection({
  customer,
  sessionId,
  paymentLinkId,
  selectedDigitalWallet,
  clickToPaySessionPending,
  isDigitalWalletValid,
  setIsDigitalWalletValid,
}: PaymentDetailsSectionProps) {
  const intl = useIntl();
  const { showPaymentDetails } = usePreselectVariant();

  const isApplePayReducedForm = useApplePayReducedForm();

  const needsDigitalWalletFields =
    selectedDigitalWallet &&
    selectedDigitalWallet !== 'clickToPay' &&
    (selectedDigitalWallet !== 'applePay' || isApplePayReducedForm);

  const renderDetails = () => {
    if (!showPaymentDetails) return null;

    if (needsDigitalWalletFields) {
      return (
        <div className="flex flex-col p-jade-250 bg-jade-background-surface rounded-xl gap-jade-200 min-500:p-0 min-500:rounded-none">
          {selectedDigitalWallet !== 'applePay' && (
            <Text variant="heading-small" color="high">
              {intl.formatMessage({ id: 'customerForm.title' })}
            </Text>
          )}
          <DigitalWalletsRequiredFields
            digitalWallet={selectedDigitalWallet}
            setIsDigitalWalletValid={setIsDigitalWalletValid}
            isDigitalWalletValid={isDigitalWalletValid}
          />
        </div>
      );
    }

    if (clickToPaySessionPending) {
      return (
        <div className="flex justify-center items-center w-full gap-jade-150 mt-jade-300 mb-jade-300 flex-col">
          <Spinner size="large" color="brand" />
        </div>
      );
    }

    return (
      <CustomerForm
        customer={customer}
        sessionId={sessionId}
        paymentLinkId={paymentLinkId}
      />
    );
  };

  return (
    <>
      <div className="block w-full py-2">
        <Divider direction="horizontal" size="small" />
      </div>
      {renderDetails()}
    </>
  );
}
